import { Link } from 'react-router-dom'
import { ArrowLeft, Calendar, Cpu, Loader2, ChevronLeft, ChevronRight, Volume2, Image as ImageIcon, Grid3x3, List, DownloadCloud } from 'lucide-react'
import { useHistoryData } from '../hooks/useHistoryData'
import { HistoryGridView } from '../components/history/HistoryGridView'
import { HistoryListView } from '../components/history/HistoryListView'

export function HistoryPage() {
  const state = useHistoryData()

  return (
    <div className="min-h-screen bg-[#0d1117] text-white">
      {/* Header */}
      <header className="border-b border-cyan-900/30 bg-[#161b22]">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Link to="/" className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span>返回主页</span>
              </Link>
              <div className="h-6 w-px bg-white/20"></div>
              <Calendar className="w-8 h-8 text-cyan-400" />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                生成历史
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {state.error && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-6">
              <p className="text-red-400">{state.error}</p>
            </div>
          )}

          {/* Media Type Filter */}
          <div className="flex items-center justify-center mb-8">
            <div className="bg-[#161b22] rounded-xl p-1 border border-cyan-900/30">
              <div className="flex space-x-1">
                {[
                  { key: 'all', label: '全部', icon: Calendar },
                  { key: 'image', label: '图像生成', icon: Cpu },
                  { key: 'vision', label: '图像理解', icon: ImageIcon },
                  { key: 'audio', label: '语音', icon: Volume2 }
                ].map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => {
                      state.setMediaType(key as any)
                      state.loadHistory(1, key as any)
                    }}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      state.mediaType === key
                        ? 'bg-cyan-500 text-white shadow-lg'
                        : 'text-gray-400 hover:text-white hover:bg-cyan-900/20'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Stats and Actions */}
          <div className="flex items-center justify-between mb-6">
            <span className="text-sm text-gray-400">共 {state.totalItems} 条记录</span>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-400">第 {state.currentPage} / {state.totalPages} 页</span>

              {state.totalItems > 0 && state.mediaType !== 'all' && (
                <button
                  onClick={state.handleDownloadAll}
                  disabled={state.downloadingAll}
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  title={`下载所有${state.mediaType === 'image' ? '图像' : state.mediaType === 'audio' ? '音频' : '文件'}`}
                >
                  {state.downloadingAll ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /><span>下载中...</span></>
                  ) : (
                    <><DownloadCloud className="w-4 h-4" /><span>下载全部</span></>
                  )}
                </button>
              )}

              {/* View Mode Toggle */}
              <div className="flex items-center space-x-1 bg-[#161b22] rounded-lg p-1 border border-cyan-900/30">
                <button
                  onClick={() => state.setViewMode('list')}
                  className={`p-2 rounded transition-colors ${
                    state.viewMode === 'list' ? 'bg-cyan-500 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                  title="列表视图"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => state.setViewMode('grid')}
                  className={`p-2 rounded transition-colors ${
                    state.viewMode === 'grid' ? 'bg-cyan-500 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                  title="网格视图"
                >
                  <Grid3x3 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          {state.loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
              <span className="ml-2 text-gray-400">加载中...</span>
            </div>
          ) : state.history.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-4">
                {state.mediaType === 'image' && '暂无图像生成记录'}
                {state.mediaType === 'vision' && '暂无图像理解记录'}
                {state.mediaType === 'audio' && '暂无语音生成记录'}
                {state.mediaType === 'all' && '暂无生成记录'}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {(state.mediaType === 'all' || state.mediaType === 'image' || state.mediaType === 'vision') && (
                  <Link to="/imagen" className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 rounded-lg font-medium transition-colors">
                    <span>开始使用图像功能</span>
                  </Link>
                )}
                {(state.mediaType === 'all' || state.mediaType === 'audio') && (
                  <Link to="/tts" className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 rounded-lg font-medium transition-colors">
                    <span>生成第一段语音</span>
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <>
              {state.viewMode === 'grid' && (
                <HistoryGridView
                  history={state.history}
                  copiedId={state.copiedId}
                  onDownload={state.handleDownload}
                  onDelete={state.handleDelete}
                  onCopyPrompt={state.handleCopyPrompt}
                />
              )}

              {state.viewMode === 'list' && (
                <HistoryListView
                  history={state.history}
                  copiedId={state.copiedId}
                  onDownload={state.handleDownload}
                  onDelete={state.handleDelete}
                  onCopyPrompt={state.handleCopyPrompt}
                />
              )}

              {/* Pagination */}
              {state.totalPages > 1 && (
                <div className="flex items-center justify-center space-x-2">
                  <button
                    onClick={() => state.handlePageChange(state.currentPage - 1)}
                    disabled={state.currentPage === 1}
                    className="flex items-center space-x-1 px-3 py-2 rounded-lg bg-cyan-900/20 hover:bg-cyan-900/30 disabled:bg-cyan-900/10 disabled:text-gray-500 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>上一页</span>
                  </button>

                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, state.totalPages) }, (_, i) => {
                      const pageNum = Math.max(1, Math.min(state.totalPages - 4, state.currentPage - 2)) + i
                      return (
                        <button
                          key={pageNum}
                          onClick={() => state.handlePageChange(pageNum)}
                          className={`px-3 py-2 rounded-lg transition-colors ${
                            pageNum === state.currentPage
                              ? 'bg-cyan-500 text-white'
                              : 'bg-cyan-900/20 hover:bg-cyan-900/30 text-gray-300'
                          }`}
                        >
                          {pageNum}
                        </button>
                      )
                    })}
                  </div>

                  <button
                    onClick={() => state.handlePageChange(state.currentPage + 1)}
                    disabled={state.currentPage === state.totalPages}
                    className="flex items-center space-x-1 px-3 py-2 rounded-lg bg-cyan-900/20 hover:bg-cyan-900/30 disabled:bg-cyan-900/10 disabled:text-gray-500 transition-colors"
                  >
                    <span>下一页</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  )
}
